import {
	USER_LIMITS,
	USER_MAIN_ROLES,
	type UserMainRole,
} from "@zplanem/responses"
import { type Locale, SUPPORTED_LOCALES } from "@zplanem/translations"
import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../lib/api-client"
import { useAuth } from "../auth-context"
import { PageContainer } from "../components/page-container"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"
import { canConfirmDeleteAccount } from "./profile-delete-account-model"

function fillTemplate(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""))
}

export function ProfilePage() {
	const navigate = useNavigate()
	const { logout } = useAuth()
	const { profile, patchProfile, deleteAccount } = useAuthenticatedUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const deleteAccountConfirmationPhrase =
		t.profile.deleteAccount.confirmationPhrase

	const [firstName, setFirstName] = useState<string>(profile.firstName ?? "")
	const [lastName, setLastName] = useState<string>(profile.lastName ?? "")
	const [language, setLanguage] = useState<Locale>(
		(profile.language as Locale | null) ?? locale,
	)
	const [aiContext, setAiContext] = useState<string>(profile.aiContext ?? "")
	const [mainRole, setMainRole] = useState<UserMainRole>(profile.mainRole)
	const [errors, setErrors] = useState<{
		firstName?: string
		lastName?: string
		aiContext?: string
		form?: string
	}>({})
	const [submitting, setSubmitting] = useState(false)
	const [savedAt, setSavedAt] = useState<number | null>(null)
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [deletePhrase, setDeletePhrase] = useState("")
	const [deleteSubmitting, setDeleteSubmitting] = useState(false)
	const [deleteError, setDeleteError] = useState<string | null>(null)

	useEffect(() => {
		setFirstName(profile.firstName ?? "")
		setLastName(profile.lastName ?? "")
		setLanguage((profile.language as Locale | null) ?? locale)
		setAiContext(profile.aiContext ?? "")
		setMainRole(profile.mainRole)
	}, [profile, locale])

	useEffect(() => {
		if (savedAt === null) return
		const id = window.setTimeout(() => setSavedAt(null), 2000)
		return () => window.clearTimeout(id)
	}, [savedAt])

	function validate(): boolean {
		const next: typeof errors = {}
		const trimmedFirst = firstName.trim()
		if (trimmedFirst.length === 0) {
			next.firstName = t.profile.errors.firstNameRequired
		} else if (trimmedFirst.length > USER_LIMITS.firstNameMax) {
			next.firstName = fillTemplate(t.profile.errors.firstNameTooLong, {
				max: USER_LIMITS.firstNameMax,
			})
		}
		const trimmedLast = lastName.trim()
		if (trimmedLast.length === 0) {
			next.lastName = t.profile.errors.lastNameRequired
		} else if (trimmedLast.length > USER_LIMITS.lastNameMax) {
			next.lastName = fillTemplate(t.profile.errors.lastNameTooLong, {
				max: USER_LIMITS.lastNameMax,
			})
		}
		const trimmedCtx = aiContext.trim()
		if (trimmedCtx.length > USER_LIMITS.aiContextMax) {
			next.aiContext = fillTemplate(t.profile.errors.aiContextTooLong, {
				max: USER_LIMITS.aiContextMax,
			})
		}
		setErrors(next)
		return Object.keys(next).length === 0
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!validate()) return
		setSubmitting(true)
		setErrors((prev) => ({ ...prev, form: undefined }))
		try {
			await patchProfile({
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				language,
				aiContext: aiContext.trim(),
				mainRole,
			})
			setSavedAt(Date.now())
		} catch (e) {
			const message =
				e instanceof ApiError ? e.message : t.profile.errors.unknown
			setErrors((prev) => ({ ...prev, form: message }))
		} finally {
			setSubmitting(false)
		}
	}

	async function onDeleteAccount() {
		if (
			!canConfirmDeleteAccount(
				deletePhrase,
				deleteAccountConfirmationPhrase,
				deleteSubmitting,
			)
		) {
			return
		}
		setDeleteSubmitting(true)
		setDeleteError(null)
		try {
			await deleteAccount()
			await logout()
			navigate(webPaths.landing(), { replace: true })
		} catch (e) {
			const message =
				e instanceof ApiError ? e.message : t.profile.deleteAccount.error
			setDeleteError(message)
			setDeleteSubmitting(false)
		}
	}

	return (
		<div className="min-h-screen w-full flex flex-col">
			<PageContainer variant="narrow" chrome="bare">
				<div className="mb-8 flex items-center justify-between gap-4">
					<h1 className="text-4xl font-black text-foreground tracking-tight">
						{t.profile.title}
					</h1>
					<Button
						type="button"
						variant="ghost"
						onClick={logout}
						className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
					>
						{t.sidebar.signOut}
					</Button>
				</div>

				<form onSubmit={onSubmit} className="space-y-6" noValidate>
					<div>
						<label
							htmlFor="firstName"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.profile.firstNameLabel}
						</label>
						<input
							id="firstName"
							type="text"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							placeholder={t.profile.firstNamePlaceholder}
							maxLength={USER_LIMITS.firstNameMax + 10}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							aria-invalid={Boolean(errors.firstName)}
							aria-describedby={
								errors.firstName ? "firstName-error" : undefined
							}
						/>
						{errors.firstName ? (
							<p id="firstName-error" className="text-sm text-destructive mt-1">
								{errors.firstName}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="lastName"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.profile.lastNameLabel}
						</label>
						<input
							id="lastName"
							type="text"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							placeholder={t.profile.lastNamePlaceholder}
							maxLength={USER_LIMITS.lastNameMax + 10}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							aria-invalid={Boolean(errors.lastName)}
							aria-describedby={errors.lastName ? "lastName-error" : undefined}
						/>
						{errors.lastName ? (
							<p id="lastName-error" className="text-sm text-destructive mt-1">
								{errors.lastName}
							</p>
						) : null}
					</div>

					<div>
						<label
							htmlFor="language"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.profile.languageLabel}
						</label>
						<select
							id="language"
							value={language}
							onChange={(e) => setLanguage(e.target.value as Locale)}
							className="form-select w-full rounded-xl bg-muted dark:bg-muted/60 border border-border pl-4 pr-9 py-3 text-foreground focus:border-ring outline-none"
						>
							{SUPPORTED_LOCALES.map((loc) => (
								<option key={loc} value={loc}>
									{t.language[loc]}
								</option>
							))}
						</select>
					</div>

					<div>
						<label
							htmlFor="mainRole"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.profile.mainRoleLabel}
						</label>
						<select
							id="mainRole"
							value={mainRole}
							onChange={(e) => setMainRole(e.target.value as UserMainRole)}
							className="form-select w-full rounded-xl bg-muted dark:bg-muted/60 border border-border pl-4 pr-9 py-3 text-foreground focus:border-ring outline-none"
						>
							{USER_MAIN_ROLES.map((role) => (
								<option key={role} value={role}>
									{t.userMainRoles[role]}
								</option>
							))}
						</select>
					</div>

					<div>
						<label
							htmlFor="aiContext"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.profile.aiContextLabel}
						</label>
						<textarea
							id="aiContext"
							value={aiContext}
							onChange={(e) => setAiContext(e.target.value)}
							placeholder={t.profile.aiContextPlaceholder}
							rows={5}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none resize-y"
							maxLength={USER_LIMITS.aiContextMax + 10}
							aria-invalid={Boolean(errors.aiContext)}
							aria-describedby={
								errors.aiContext ? "aiContext-error" : undefined
							}
						/>
						{errors.aiContext ? (
							<p id="aiContext-error" className="text-sm text-destructive mt-1">
								{errors.aiContext}
							</p>
						) : null}
					</div>

					{errors.form ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
						>
							{errors.form}
						</div>
					) : null}

					{savedAt !== null ? (
						<div
							role="status"
							className="rounded-xl border border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-300 text-sm px-4 py-3"
						>
							{t.profile.saved}
						</div>
					) : null}

					<div className="flex items-center gap-3 pt-2">
						<Button
							type="submit"
							variant="brand"
							size="lg"
							disabled={submitting}
						>
							{t.profile.save}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="lg"
							onClick={() => navigate(webPaths.dashboard())}
							disabled={submitting}
						>
							{t.profile.cancel}
						</Button>
					</div>
				</form>

				<section
					className="mt-12 border-t border-destructive/25 pt-6"
					aria-labelledby="delete-account-title"
				>
					<h2
						id="delete-account-title"
						className="text-lg font-semibold text-destructive"
					>
						{t.profile.deleteAccount.sectionTitle}
					</h2>
					<p className="mt-2 text-sm text-muted-foreground">
						{t.profile.deleteAccount.sectionBody}
					</p>
					<Button
						type="button"
						variant="link"
						className="mt-3 px-0 text-destructive hover:text-destructive"
						onClick={() => {
							setDeleteDialogOpen(true)
							setDeletePhrase("")
							setDeleteError(null)
						}}
					>
						{t.profile.deleteAccount.open}
					</Button>
				</section>
			</PageContainer>

			{deleteDialogOpen ? (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="delete-account-dialog-title"
					className="fixed inset-0 z-50 flex min-h-dvh items-center justify-center bg-background/95 px-4 py-6"
				>
					<div className="w-full max-w-lg rounded-xl border border-border bg-background p-6 shadow-xl">
						<h2
							id="delete-account-dialog-title"
							className="text-2xl font-bold tracking-tight text-foreground"
						>
							{t.profile.deleteAccount.dialogTitle}
						</h2>
						<p className="mt-3 text-sm leading-6 text-muted-foreground">
							{t.profile.deleteAccount.dialogBody}
						</p>

						<label
							htmlFor="delete-account-phrase"
							className="mt-6 block text-sm font-semibold text-foreground/80"
						>
							{fillTemplate(t.profile.deleteAccount.phraseLabel, {
								phrase: deleteAccountConfirmationPhrase,
							})}
						</label>
						<input
							id="delete-account-phrase"
							type="text"
							value={deletePhrase}
							onChange={(e) => setDeletePhrase(e.target.value)}
							disabled={deleteSubmitting}
							className="mt-2 w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground outline-none focus:border-ring"
							autoComplete="off"
							aria-describedby={
								deleteError ? "delete-account-error" : undefined
							}
						/>

						{deleteError ? (
							<p
								id="delete-account-error"
								role="alert"
								className="mt-3 text-sm text-destructive"
							>
								{deleteError}
							</p>
						) : null}

						<div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
							<Button
								type="button"
								variant="ghost"
								onClick={() => setDeleteDialogOpen(false)}
								disabled={deleteSubmitting}
							>
								{t.profile.deleteAccount.cancel}
							</Button>
							<Button
								type="button"
								variant="destructive"
								onClick={() => void onDeleteAccount()}
								disabled={
									!canConfirmDeleteAccount(
										deletePhrase,
										deleteAccountConfirmationPhrase,
										deleteSubmitting,
									)
								}
							>
								{deleteSubmitting
									? t.profile.deleteAccount.submitting
									: t.profile.deleteAccount.confirm}
							</Button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	)
}
